import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Sparkles, QrCode, Printer, Ticket, CheckCircle2, Copy, Loader2, Pause, Play, Users } from "lucide-react";

const PASOS = [
  "Imprime tu QR único y pégalo en el mostrador, la puerta o tus bolsas.",
  "Tu cliente lo escanea: se instala su Cuponera en el celular (sin registro, solo celular y C.P.).",
  "Aquí creas hasta 5 cupones cada 30 días; la IA diseña la imagen con tu marca y oferta.",
  "El cliente toca 'Quiero este cupón' y recibe un código de 6 letras.",
  "Al pagar, te muestra el código; lo escribes en 'Canjear' y el descuento queda registrado.",
  "Tus cupones también aparecen en la Cuponera de clientes de otros comercios afiliados cercanos.",
];

const ESTILOS = ["festivo", "elegante", "divertido", "urbano", "minimalista", "premium"];
const hoy = () => new Date().toISOString().slice(0, 10);
const en30 = () => new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
const FORM_INIT = { titulo: "", producto_aplica: "", tipo_descuento: "porcentaje", valor: "", vigencia_inicio: hoy(), vigencia_fin: en30(), condiciones: "", estilo: "festivo", colores: "", mensaje: "", elementos: "" };

export const CuponerasTab = () => {
  const [info, setInfo] = useState(null);
  const [cupones, setCupones] = useState([]);
  const load = () => {
    api.get("/cuponera/mi-codigo").then(r => setInfo(r.data)).catch(() => {});
    api.get("/cupones/mios").then(r => setCupones(r.data.cupones)).catch(() => {});
  };
  useEffect(load, []);

  return (
    <div className="space-y-6" data-testid="cuponeras-tab">
      <div className="bg-zinc-900 text-white rounded-sm p-6 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <div className="overline text-[#D4FF00]">CUPONERAS</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1">Haz marketing con cupones</div>
          <p className="text-white/70 text-sm mt-2 max-w-xl">La IA diseña tus cupones, tus clientes los reciben en su celular al escanear tu QR y tú controlas cada canje.</p>
          <ol className="mt-4 grid sm:grid-cols-2 gap-2 text-sm">
            {PASOS.map((p, i) => (
              <li key={`paso-${i}`} className="flex gap-2 bg-white/5 rounded-sm p-2.5"><span className="font-display font-black text-[#FF3366]">{String(i + 1).padStart(2, "0")}</span><span className="text-white/85">{p}</span></li>
            ))}
          </ol>
        </div>
        <div className="flex gap-3 text-center">
          <Stat label="Cupones disponibles" value={info ? `${info.cupones_restantes_30d}/5` : "—"} testId="cupones-restantes" />
          <Stat label="Suscriptores" value={info?.suscriptores ?? "—"} testId="cupones-suscriptores" Icon={Users} />
        </div>
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-6">
        {info && <QRCard info={info} />}
        <NuevoCupon info={info} onCreated={load} />
      </div>

      <Canjear onDone={load} />

      <section>
        <div className="overline text-zinc-500 mb-3">Mis cupones ({cupones.length})</div>
        {cupones.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-10 text-center text-zinc-400 text-sm">Aún no has creado cupones. ¡Diseña el primero con IA!</div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{cupones.map(c => <CuponCard key={c.id} c={c} onChange={load} />)}</div>
        )}
      </section>
    </div>
  );
};

function Stat({ label, value, testId, Icon = Ticket }) {
  return (
    <div className="bg-white/10 rounded-sm px-5 py-4 min-w-[130px]">
      <Icon className="w-4 h-4 mx-auto text-[#D4FF00]" />
      <div className="font-display font-black text-3xl tracking-tighter mt-1" data-testid={testId}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-white/60">{label}</div>
    </div>
  );
}

function QRCard({ info }) {
  const ref = useRef(null);
  const descargar = () => {
    const canvas = ref.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a"); a.href = canvas.toDataURL("image/png"); a.download = `QR-cuponera-${info.codigo}.png`; a.click();
  };
  const imprimir = () => {
    const canvas = ref.current?.querySelector("canvas");
    const w = window.open("", "_blank");
    w.document.write(`<html><body style="font-family:Arial;text-align:center;padding:40px"><h1 style="font-size:42px;margin:0">¡Escanea y recibe cupones!</h1><p style="font-size:20px;color:#555">Instala tu Cuponera y obtén descuentos exclusivos</p><img src="${canvas.toDataURL()}" style="width:420px;height:420px;margin:24px auto;display:block"/><p style="font-size:28px;font-weight:bold;letter-spacing:4px">${info.codigo}</p><p style="color:#888">${info.url}</p></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5 flex flex-col items-center text-center" data-testid="qr-card">
      <div className="overline text-zinc-500 flex items-center gap-1"><QrCode className="w-3.5 h-3.5" /> Tu QR único</div>
      <div ref={ref} className="mt-3 p-3 bg-white border-4 border-zinc-900 rounded-sm">
        <QRCodeCanvas value={info.url} size={220} level="H" includeMargin={false} fgColor="#18181B" />
      </div>
      <div className="mt-3 font-mono font-black text-2xl tracking-[0.3em]" data-testid="qr-codigo">{info.codigo}</div>
      <a href={info.url} target="_blank" rel="noreferrer" className="text-xs text-[#FF3366] underline underline-offset-4 break-all mt-1" data-testid="qr-url">{info.url}</a>
      <div className="mt-4 flex gap-2 w-full">
        <Button onClick={imprimir} data-testid="qr-print" className="flex-1 rounded-sm bg-zinc-900 hover:bg-[#FF3366]"><Printer className="w-4 h-4 mr-1" /> Imprimir hoja</Button>
        <Button onClick={descargar} variant="outline" data-testid="qr-download" className="rounded-sm">PNG</Button>
      </div>
      <p className="text-[11px] text-zinc-500 mt-3">Pídele a tus clientes que lo escaneen con la cámara del celular.</p>
    </div>
  );
}

function NuevoCupon({ info, onCreated }) {
  const [form, setForm] = useState(FORM_INIT);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const agotado = info && info.cupones_restantes_30d <= 0;
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/cupones/generar", { ...form, valor: parseFloat(form.valor || 0) });
      toast.success("¡Cupón diseñado por IA!");
      setForm(FORM_INIT); onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-sm p-5 space-y-3" data-testid="nuevo-cupon-form">
      <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FF3366]" /><div className="font-display font-bold text-lg tracking-tight">Diseña un cupón con IA</div></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Título de la promo"><Input data-testid="cup-titulo" required value={form.titulo} onChange={e => set("titulo", e.target.value)} placeholder="Ej. Semana de moda" className="rounded-sm" /></F>
        <F label="Producto o artículo donde aplica"><Input data-testid="cup-producto" required value={form.producto_aplica} onChange={e => set("producto_aplica", e.target.value)} placeholder="Ej. Toda la línea de vestidos" className="rounded-sm" /></F>
        <F label="Tipo de descuento">
          <Select value={form.tipo_descuento} onValueChange={v => set("tipo_descuento", v)}>
            <SelectTrigger data-testid="cup-tipo" className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="porcentaje">% de descuento</SelectItem>
              <SelectItem value="monto">$ de descuento</SelectItem>
              <SelectItem value="2x1">2x1</SelectItem>
              <SelectItem value="regalo">Regalo con compra</SelectItem>
            </SelectContent>
          </Select>
        </F>
        <F label={form.tipo_descuento === "porcentaje" ? "Porcentaje (%)" : form.tipo_descuento === "monto" ? "Monto ($ MXN)" : "Valor (opcional)"}>
          <Input data-testid="cup-valor" type="number" min="0" step="1" value={form.valor} onChange={e => set("valor", e.target.value)} required={["porcentaje", "monto"].includes(form.tipo_descuento)} className="rounded-sm" />
        </F>
        <F label="Vigencia desde"><Input data-testid="cup-inicio" type="date" value={form.vigencia_inicio} onChange={e => set("vigencia_inicio", e.target.value)} required className="rounded-sm" /></F>
        <F label="Vigencia hasta"><Input data-testid="cup-fin" type="date" value={form.vigencia_fin} onChange={e => set("vigencia_fin", e.target.value)} required className="rounded-sm" /></F>
        <F label="Condiciones para ser válido" className="sm:col-span-2"><Input data-testid="cup-condiciones" value={form.condiciones} onChange={e => set("condiciones", e.target.value)} placeholder="Ej. No acumulable. Compra mínima $300. Solo en tienda física." className="rounded-sm" /></F>
        <F label="Estilo del diseño">
          <Select value={form.estilo} onValueChange={v => set("estilo", v)}>
            <SelectTrigger data-testid="cup-estilo" className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{ESTILOS.map(e => <SelectItem key={e} value={e} className="capitalize">{e}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Colores de tu marca"><Input data-testid="cup-colores" value={form.colores} onChange={e => set("colores", e.target.value)} placeholder="Ej. negro, dorado y rosa" className="rounded-sm" /></F>
        <F label="Mensaje o eslogan"><Input data-testid="cup-mensaje" value={form.mensaje} onChange={e => set("mensaje", e.target.value)} placeholder="Ej. Renueva tu closet" className="rounded-sm" /></F>
        <F label="Elementos visuales"><Input data-testid="cup-elementos" value={form.elementos} onChange={e => set("elementos", e.target.value)} placeholder="Ej. tacos, confeti, iconos de moda" className="rounded-sm" /></F>
      </div>
      <Button type="submit" data-testid="cup-generar" disabled={loading || agotado} className="w-full h-12 rounded-sm bg-[#FF3366] hover:bg-[#E62958] font-display font-bold">
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> La IA está diseñando tu cupón (15–30 s)…</> : agotado ? "Límite de 5 cupones en 30 días alcanzado" : <><Sparkles className="w-4 h-4 mr-2" /> Generar cupón con IA</>}
      </Button>
    </form>
  );
}

function F({ label, children, className = "" }) {
  return <div className={className}><Label className="text-xs uppercase tracking-wider text-zinc-500">{label}</Label><div className="mt-1">{children}</div></div>;
}

function Canjear({ onDone }) {
  const [codigo, setCodigo] = useState("");
  const [res, setRes] = useState(null);
  const canjear = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/cupones/canjear", { codigo });
      setRes(data); setCodigo(""); toast.success("Cupón canjeado"); onDone();
    } catch (e) { setRes(null); toast.error(formatApiError(e)); }
  };
  return (
    <form onSubmit={canjear} className="bg-[#D4FF00] rounded-sm p-5 flex flex-wrap items-center gap-3" data-testid="canjear-form">
      <div className="flex-1 min-w-[200px]">
        <div className="font-display font-black text-xl tracking-tight">Canjear cupón del cliente</div>
        <div className="text-xs text-zinc-700">Escribe el código de 6 letras que te muestra el cliente en su Cuponera.</div>
      </div>
      <Input data-testid="canjear-codigo" value={codigo} onChange={e => setCodigo(e.target.value.toUpperCase())} maxLength={6} placeholder="ABC123" className="w-40 h-12 rounded-sm font-mono text-2xl tracking-[0.3em] text-center bg-white" />
      <Button type="submit" data-testid="canjear-btn" disabled={codigo.length < 6} className="h-12 rounded-sm bg-zinc-900 hover:bg-[#FF3366]"><CheckCircle2 className="w-4 h-4 mr-1" /> Canjear</Button>
      {res && <div data-testid="canje-ok" className="w-full text-sm bg-white rounded-sm p-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Canjeado: <b>{res.cupon.titulo}</b> · {res.cupon.descuento_texto}{res.cliente?.telefono ? ` · cliente ${res.cliente.telefono}` : ""}</div>}
    </form>
  );
}

function CuponCard({ c, onChange }) {
  const toggle = async () => {
    try { await api.patch(`/cupones/${c.id}/estado`, null, { params: { estado: c.estado === "activo" ? "pausado" : "activo" } }); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid={`cupon-${c.id}`} className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
      <img src={c.imagen_url} alt={c.titulo} className="w-full aspect-[3/2] object-cover" />
      <div className="p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display font-bold tracking-tight flex-1">{c.titulo}</div>
          <Badge className={`border-0 ${c.estado === "activo" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>{c.estado}</Badge>
        </div>
        <div className="text-sm text-[#FF3366] font-bold">{c.descuento_texto} · {c.producto_aplica}</div>
        <div className="text-xs text-zinc-500 mt-1">Vigencia {c.vigencia_inicio} → {c.vigencia_fin}</div>
        {c.condiciones && <div className="text-[11px] text-zinc-500 mt-1">{c.condiciones}</div>}
        <div className="mt-3 flex items-center gap-3 text-xs text-zinc-600">
          <span><b>{c.reclamados}</b> reclamados</span><span><b>{c.canjeados}</b> canjeados</span>
          <button onClick={toggle} data-testid={`cupon-toggle-${c.id}`} className="ml-auto inline-flex items-center gap-1 text-zinc-900 hover:text-[#FF3366]">{c.estado === "activo" ? <><Pause className="w-3.5 h-3.5" /> Pausar</> : <><Play className="w-3.5 h-3.5" /> Activar</>}</button>
          <button onClick={() => { navigator.clipboard?.writeText(c.imagen_url); toast.success("Imagen copiada (data URL)"); }} className="text-zinc-400 hover:text-zinc-900"><Copy className="w-3.5 h-3.5" /></button>
        </div>
      </div>
    </div>
  );
}
